/**
 * Campaign Service
 *
 * Handles all API calls related to campaigns:
 * - CRUD operations
 * - API key regeneration
 * - Statistics fetching
 */

import api from './api';
import type {
  Campaign,
  CampaignResponse,
  CampaignsResponse,
  CreateCampaignData,
  UpdateCampaignData,
  RegenerateApiKeyResponse,
} from '../types/campaign';
import type { CampaignStatistics } from '../types/statistics';
import type { BulkOperationResponse } from '../types/campaign';

export interface CampaignFilters {
  search?: string;
  status?: string[];
  managerId?: string;
  tagIds?: number[];
  createdFrom?: string;
  createdTo?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export interface PaginatedCampaignsResponse {
  campaigns: Campaign[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
}

/**
 * Fetch all campaigns for the authenticated user's account
 * @deprecated Use getCampaigns with filters instead
 */
export async function getAllCampaigns(): Promise<Campaign[]> {
  const response = await api.get<CampaignsResponse>('/api/campaigns');
  return response.data.campaigns;
}

/**
 * Fetch campaigns with optional filters, sorting, and pagination
 */
export async function getCampaigns(filters?: CampaignFilters): Promise<PaginatedCampaignsResponse> {
  const params = new URLSearchParams();

  if (filters?.search) {
    params.append('search', filters.search);
  }

  if (filters?.status && filters.status.length > 0) {
    filters.status.forEach(s => params.append('status[]', s));
  }

  if (filters?.managerId) {
    params.append('manager_id', filters.managerId);
  }

  if (filters?.tagIds && filters.tagIds.length > 0) {
    filters.tagIds.forEach(id => params.append('tag_ids[]', id.toString()));
  }

  if (filters?.createdFrom) {
    params.append('created_from', filters.createdFrom);
  }

  if (filters?.createdTo) {
    params.append('created_to', filters.createdTo);
  }

  if (filters?.sort) {
    params.append('sort', filters.sort);
  }

  if (filters?.direction) {
    params.append('direction', filters.direction);
  }

  if (filters?.page) {
    params.append('page', filters.page.toString());
  }

  if (filters?.perPage) {
    params.append('per_page', filters.perPage.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `/api/campaigns?${queryString}` : '/api/campaigns';

  const response = await api.get<PaginatedCampaignsResponse>(url);
  return response.data;
}

/**
 * Fetch a single campaign by ID
 */
export async function getCampaign(id: number): Promise<Campaign> {
  const response = await api.get<CampaignResponse>(`/api/campaigns/${id}`);
  return response.data.campaign;
}

/**
 * Create a new campaign
 */
export async function createCampaign(data: CreateCampaignData): Promise<Campaign> {
  const response = await api.post<CampaignResponse>('/api/campaigns', data);
  return response.data.campaign;
}

/**
 * Update an existing campaign
 */
export async function updateCampaign(
  id: number,
  data: UpdateCampaignData
): Promise<Campaign> {
  const response = await api.put<CampaignResponse>(`/api/campaigns/${id}`, data);
  return response.data.campaign;
}

/**
 * Delete a campaign
 */
export async function deleteCampaign(id: number): Promise<void> {
  await api.delete(`/api/campaigns/${id}`);
}

/**
 * Regenerate API key for a campaign
 */
export async function regenerateApiKey(id: number): Promise<Campaign> {
  const response = await api.post<RegenerateApiKeyResponse>(
    `/api/campaigns/${id}/regenerate-api-key`
  );
  return response.data.campaign;
}

/**
 * Fetch campaign statistics
 */
export async function getCampaignStatistics(id: number): Promise<CampaignStatistics> {
  const response = await api.get<{ statistics: CampaignStatistics }>(
    `/api/campaigns/${id}/statistics`
  );
  return response.data.statistics;
}

/**
 * Fetch paginated campaign request logs
 */
export interface RequestLogsParams {
  page?: number;
  per_page?: number;
  period?: string;
  start_date?: string;
  end_date?: string;
}

export interface RequestLogsPaginatedResponse {
  data: any[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
}

export async function getCampaignRequestLogs(
  id: number,
  params?: RequestLogsParams
): Promise<RequestLogsPaginatedResponse> {
  const queryParams = new URLSearchParams();

  if (params?.page) {
    queryParams.append('page', params.page.toString());
  }
  if (params?.per_page) {
    queryParams.append('per_page', params.per_page.toString());
  }
  if (params?.period) {
    queryParams.append('period', params.period);
  }
  if (params?.start_date) {
    queryParams.append('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.append('end_date', params.end_date);
  }

  const response = await api.get<RequestLogsPaginatedResponse>(
    `/api/campaigns/${id}/request-logs?${queryParams.toString()}`
  );
  return response.data;
}

/**
 * Duplicate an existing campaign with a new name
 */
export async function duplicateCampaign(id: number, name: string): Promise<Campaign> {
  const response = await api.post<CampaignResponse>(
    `/api/campaigns/${id}/duplicate`,
    { name }
  );
  return response.data.campaign;
}

/**
 * Bulk update campaign status
 */
export async function bulkUpdateStatus(
  campaignIds: number[],
  status: string
): Promise<BulkOperationResponse> {
  const response = await api.put<BulkOperationResponse>(
    '/api/campaigns/bulk-update-status',
    { campaign_ids: campaignIds, status }
  );
  return response.data;
}

/**
 * Bulk delete campaigns
 */
export async function bulkDelete(campaignIds: number[]): Promise<BulkOperationResponse> {
  const response = await api.delete<BulkOperationResponse>(
    '/api/campaigns/bulk-delete',
    { data: { campaign_ids: campaignIds } }
  );
  return response.data;
}

/**
 * Bulk assign manager to campaigns
 */
export async function bulkAssignManager(
  campaignIds: number[],
  managerId: number | null
): Promise<BulkOperationResponse> {
  const response = await api.put<BulkOperationResponse>(
    '/api/campaigns/bulk-assign-manager',
    { campaign_ids: campaignIds, manager_id: managerId }
  );
  return response.data;
}

/**
 * Bulk apply tags to campaigns
 */
export async function bulkApplyTags(
  campaignIds: number[],
  tagIds: number[],
  action: 'add' | 'remove' | 'replace'
): Promise<BulkOperationResponse> {
  const response = await api.put<BulkOperationResponse>(
    '/api/campaigns/bulk-apply-tags',
    { campaign_ids: campaignIds, tag_ids: tagIds, action }
  );
  return response.data;
}

/**
 * Fetch A/B test variant performance statistics for a campaign
 */
export async function getCampaignABStats(
  id: number,
  period: string = '30d'
): Promise<import('../types/campaign').ABVariantStat[]> {
  const response = await api.get<{ stats: import('../types/campaign').ABVariantStat[] }>(
    `/api/campaigns/${id}/ab-stats?period=${period}`
  );
  return response.data.stats;
}

export const campaignService = {
  getAllCampaigns,
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  regenerateApiKey,
  getCampaignStatistics,
  getCampaignRequestLogs,
  duplicateCampaign,
  bulkUpdateStatus,
  bulkDelete,
  bulkAssignManager,
  bulkApplyTags,
  getCampaignABStats,
};
